import { useState } from 'react'
import { Plus, Edit, Trash2, ExternalLink, ShoppingBag, Eye, EyeOff } from 'lucide-react'
import { useShopProducts, useDeleteProduct, useUpdateProduct } from '@/hooks/use-shop'
import type { Tables } from '@/types/supabase'
import ProductFormModal from '@/components/shop/product-form-modal'
import { FeatureGate } from '@/components/feature-gate'

type ShopProduct = Tables<'shop_products'>

function formatRupiah(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

export default function ShopPage() {
  const { data: products = [], isLoading } = useShopProducts()
  const deleteProduct = useDeleteProduct()
  const updateProduct = useUpdateProduct()

  const [modalOpen, setModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ShopProduct | undefined>(undefined)

  function openCreate() {
    setEditingProduct(undefined)
    setModalOpen(true)
  }

  function openEdit(product: ShopProduct) {
    setEditingProduct(product)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingProduct(undefined)
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat produk...</p>

  return (
    <FeatureGate feature="shop">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shop</h1>
          <p className="text-muted-foreground">Kelola katalog merchandise dan produk</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Tambah Produk
        </button>
      </div>

      {products.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <ShoppingBag className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="font-medium">Belum ada produk</p>
          <p className="text-sm text-muted-foreground mt-1">Tambahkan merchandise atau produk pertama Anda.</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Tambah Produk
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <div
              key={product.id}
              className="group rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Cover photo */}
              <div className="aspect-square bg-muted relative overflow-hidden">
                {product.cover_url ? (
                  <img
                    src={product.cover_url}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ShoppingBag className="h-12 w-12 text-muted-foreground/30" />
                  </div>
                )}
                {/* Availability badge */}
                <span
                  className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                    product.is_available
                      ? 'bg-green-500/90 text-white'
                      : 'bg-black/50 text-white'
                  }`}
                >
                  {product.is_available ? 'Tersedia' : 'Habis'}
                </span>
              </div>

              {/* Info */}
              <div className="p-3 space-y-1">
                <p className="font-medium text-sm leading-tight">{product.name}</p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {product.category && <span>{product.category}</span>}
                  {product.category && product.price_idr != null && <span>·</span>}
                  {product.price_idr != null && (
                    <span className="font-medium text-foreground">{formatRupiah(product.price_idr)}</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-1">
                  {product.external_url ? (
                    <a
                      href={product.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      Toko eksternal
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span />
                  )}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        updateProduct.mutate({ id: product.id, is_available: !product.is_available })
                      }
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      title={product.is_available ? 'Tandai habis' : 'Tandai tersedia'}
                    >
                      {product.is_available ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={() => openEdit(product)}
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      title="Edit produk"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Hapus produk "${product.name}"?`))
                          deleteProduct.mutate(product.id)
                      }}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title="Hapus produk"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProductFormModal
        open={modalOpen}
        onClose={closeModal}
        product={editingProduct}
      />
    </div>
    </FeatureGate>
  )
}
